import { useState, useEffect } from 'react';
import { Language, useLanguage } from '../context/LanguageContext';
import { fetchContent } from '../api/contentApi';

// Environment variables
const ENABLE_API = process.env.REACT_APP_ENABLE_API === 'true';

interface UsePageContentReturn<T> {
    content: T | null;
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch page content based on the page name and current language
 */
export function usePageContent<T>(page: string): UsePageContentReturn<T> {
    const { language } = useLanguage();
    const [content, setContent] = useState<T | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    const loadContent = async () => {
        setLoading(true);
        try {
            // In production, content comes from fetchContent which uses
            // bundled JSON files instead of API calls
            const data = await fetchContent(page, language);
            setContent(data as T);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to load content'));
            console.error(`Failed to load ${page} content:`, err);
        } finally {
            setLoading(false);
        }
    };

    // Load content when page or language changes
    useEffect(() => {
        loadContent();
    }, [page, language]);

    return {
        content,
        loading,
        error,
        refetch: loadContent
    };
} 